import { useMemo, useCallback } from 'react'
import {
  StyleShotData,
  StyleShotItem,
  ImageRequestItem,
  ImageAPIResponse,
} from '../types'
import useGetGalleryImages from '../../../../hooks/query/useGetGalleryImages'
import { downloadFile, downloadMultipleFiles } from '../../../../utils/download'

interface UseStyleShotDataProps {
  page: number
  itemsPerPage: number
  enabled?: boolean
}

// API 상태를 갤러리 상태로 매핑하는 함수
const mapAPIStatusToGalleryStatus = (
  status: string
): 'queued' | 'processing' | 'completed' | 'failed' => {
  switch (status.toLowerCase()) {
    case 'queued':
      return 'queued'
    case 'processing':
      return 'processing'
    case 'completed':
      return 'completed'
    case 'failed':
      return 'failed'
    default:
      return 'queued'
  }
}

// 이미지 데이터를 StyleShotItem으로 변환하는 함수
const transformImageDataToStyleShotItems = (
  imageRequests: ImageRequestItem[]
): StyleShotItem[] => {
  return imageRequests.map((request) => ({
    id: `${request?.requestId}-${request?.jobId}`,
    type: 'style-shot' as const,
    imageUrl: request.resultUrl,
    status: mapAPIStatusToGalleryStatus(request.jobStatus),
    createdAt: request.completedAt,
    downloadUrl: request.resultUrl,
    jobId: request.jobId,
    requestId: request.requestId,
    // style-shot 전용 필드들
    avatarId: request.avatarId,
    avatarName: request.avatarName,
    avatarThumbnailUrl: request.avatarThumbnailUrl,
    backgroundId: request.backgroundId,
    backgroundImageUrl: request.backgroundImageUrl,
    backgroundName: request.backgroundName,
    poseId: request.poseId,
    poseName: request.poseName,
  }))
}

export const useStyleShotData = ({
  page,
  itemsPerPage,
  enabled = true,
}: UseStyleShotDataProps) => {
  // 이미지 쿼리
  const imageQuery = useGetGalleryImages(page, itemsPerPage, { enabled })

  // 데이터 변환 및 메모이제이션
  const data = useMemo((): StyleShotData => {
    if (!imageQuery?.data) {
      return {
        items: [],
        totalCount: 0,
        totalPages: 0,
      }
    }

    const imageResponse = imageQuery.data as ImageAPIResponse
    const items = transformImageDataToStyleShotItems(imageResponse.items)

    return {
      items,
      totalCount: imageResponse.total,
      totalPages: imageResponse.totalPages,
    }
  }, [imageQuery.data])

  // 로딩 및 에러 상태
  const isLoading = imageQuery.isLoading
  const error = imageQuery.error ? (imageQuery.error as Error).message : null

  // 다운로드 함수
  const downloadItem = useCallback(async (item: StyleShotItem) => {
    try {
      if (!item.downloadUrl) {
        throw new Error('Download URL not available')
      }

      await downloadFile(item.downloadUrl, `style-shot-${item.id}.jpg`, {
        title: '스타일샷 이미지',
        text: '스타일샷 이미지를 저장하거나 공유하세요',
      })
    } catch (err) {
      console.error('Style shot download failed:', err)
    }
  }, [])

  // 다중 다운로드 함수
  const downloadMultiple = useCallback(
    async (items: StyleShotItem[]) => {
      const downloadableItems = items.filter(
        (item) => item.status === 'completed' && item.downloadUrl
      )

      const downloads = downloadableItems.map((item) => ({
        url: item.downloadUrl!,
        filename: `style-shot-${item.id}.jpg`,
        options: {
          title: '스타일샷 이미지',
          text: '스타일샷 이미지를 저장하거나 공유하세요',
        },
      }))

      await downloadMultipleFiles(downloads, 200)
    },
    []
  )

  return {
    data,
    isLoading,
    error,
    refetch: imageQuery.refetch,
    downloadItem,
    downloadMultiple,
  }
}